import React from 'react'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import LesaOfferCardSkeleton from './Common/LesaOfferCardSkeleton'
import { ChatActions, MessageMetaContent, MessageMetaPayload } from 'api/support'
import Action, { ActionsList } from 'supportAssistant/components/Chat/MessageMeta/Action'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import BedbankOfferPriceDetails from 'components/OfferList/OfferCards/BedbankOfferPriceDetails'
import styled from 'styled-components'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'

interface Props {
  hideSelect?: boolean;
  actions?: MessageMetaContent[];
  payload?: MessageMetaPayload;
  offer: any;
  handleAction: (message: string, meta: any) => void;
}

const imageParams: ImageParams = {
  aspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopWidth: '650px',
  quality: 'good',
  width: '260px',
}

const StyledDivWrapper = styled.div`
  display: grid;
  grid-template-areas: "images description price";
  grid-template-columns: 6fr 8fr 5fr;
  grid-column-gap: ${rem(16)};
  width: ${rem(720)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  padding-right: ${rem(12)};

  ${mediaQueryDown.tablet} {
    width: ${rem(680)};
    grid-template-areas:
      "images description"
      "images price";
    grid-template-columns: 3fr 4fr;
  }

  ${mediaQueryDown.mobile} {
    grid-column-gap: ${rem(16)};
    grid-template-areas:
      "images"
      "description"
      "price";
    grid-template-columns: 1fr;
    width: ${rem(320)};
    padding: ${rem(12)};
  }
`

const StyledImageCarouselDiv = styled.div`
  grid-area: images;

  ${mediaQueryDown.mobile} {
    height: ${rem(180)};
  }
`
const StyledContentDiv = styled.div`
  grid-area: description;
  padding: ${rem(16)} 0;
`

const StyledPriceDiv = styled.div`
  grid-area: price;
  padding: ${rem(16)} 0;
`

export default function LesaBedbankOfferCard({ hideSelect, offer, actions, handleAction, payload }: Props) {
  const location = offer?.property?.address ? [offer.property.address?.city, offer.property.address?.stateProvinceName].filter(Boolean).join(', ') : undefined

  const offerUrl = useOfferUrl(offer, {})

  if (!offer) {
    return <LesaOfferCardSkeleton />
  }

  return (
    <>
      <StyledDivWrapper>
        <StyledImageCarouselDiv>
          <ImageCarousel
            images={offer.images}
            imageParams={imageParams}
            eagerLoadFirstImage
          />
        </StyledImageCarouselDiv>
        <StyledContentDiv>
          <Group direction="vertical">
            <OfferUrgencyLabels offer={offer} />
            <CaptionTextBlock variant="large">{location}</CaptionTextBlock>
            <HeadingTextBlock variant="heading5">{offer.name}</HeadingTextBlock>
            <ProductTypeLabel productType="bedbank_hotel" kind="plain" />
          </Group>
        </StyledContentDiv>
        <StyledPriceDiv>
          <Group direction="vertical" gap={8} verticalAlign="end" fullHeight>
            <BedbankOfferPriceDetails offer={offer} loggedInButtonsAlign="left" />
            <Group horizontalAlign="stretch" fullWidth direction="horizontal-reverse" gap={4} tabletDirection="horizontal-reverse" desktopDirection="vertical">
              {!hideSelect && <TextButton onClick={() => handleAction?.('', { key: ChatActions.SELECT_OFFER, payload: { offerId: offer.id } })} size="medium" kind="primary">
                Ask about this offer
              </TextButton>}
              <TextButton to={offerUrl} target="_blank" size="medium" kind="secondary">
                View details
              </TextButton>
            </Group>
          </Group>
        </StyledPriceDiv>
      </StyledDivWrapper>
      {!!actions?.length && <ActionsList>
        {actions?.map((action) => (
          <Action
            key={action.action?.key}
            action={action}
            payload={payload}
            handleAction={(text, meta) => handleAction(text, { ...meta, orderType: 'hotel' })}
          />
        ))}
      </ActionsList>}
    </>
  )
}
